import { useQuery } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import { formatDayShort } from '@/lib/dates';
import { EMPLOYEES_WITH_STATS } from '@/data/employees';
import {
  EMPLOYEE_DETAIL_STATS,
  EMPLOYEE_CLIENT_GROUPS,
  REVIEW_ITEMS,
  REVIEW_ITEMS_TOTAL,
  REVIEW_TOTAL_HOURS,
  REVIEW_TOTAL_SESSIONS,
  APP_USAGE,
  ACTIVITY_BARS,
  DEFAULT_JIRA_WORKLOG_FIXTURE,
  JIRA_WORKLOG_FIXTURE,
} from '@/data/employeeDetail';
import type { EmployeeWithStats } from '@/data/employees';
import type {
  EmployeeDetailStat,
  EmployeeClientGroup,
  ReviewItem,
  AppUsageRow,
  JiraProjectGroup,
  JiraWorklogsFixture,
} from '@/data/employeeDetail';
import type { EmployeeFallbackSamplesResponse } from '@/types/dashboard';

// Task 127: top-N unmatched groups rendered on the employee detail page.
const REVIEW_ITEMS_LIMIT = 4;

interface UseEmployeeDetailResult {
  employee: EmployeeWithStats | undefined;
  stats: readonly EmployeeDetailStat[];
  clientGroups: readonly EmployeeClientGroup[];
  reviewItems: readonly ReviewItem[];
  /** Total number of unmatched (app, title) groups for this employee in the
   * current date range — the number shown as "see all N" on the table. */
  reviewItemsTotal: number;
  /** Sum of `totalHours` across every unmatched group (pre-limit). Drives
   * the "Needs Your Review" banner copy on /employees/:employeeId. */
  reviewTotalHours: number;
  /** Sum of `sessions` (distinct dates) across every unmatched group. */
  reviewTotalSessions: number;
  appUsage: readonly AppUsageRow[];
  activityBars: readonly number[];
  /**
   * Task 139 — "Time in Jira" section data. `enabled=false` when the
   * tenant has no Jira credential; the page hides the section.
   * `groups` may be empty when enabled but the employee has no
   * worklogs in range — the page renders the empty state.
   */
  jira: {
    enabled: boolean;
    lastSyncedAt: string | null;
    groups: readonly JiraProjectGroup[];
  };
  isLoading: boolean;
}

export function useEmployeeDetail(employeeId: string | undefined): UseEmployeeDetailResult {
  const { mockupMode, currentOrg, dateRange } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  const projectsQuery = useQuery({
    queryKey: ['employeeProjects', orgId, employeeId, dateRange],
    queryFn: async () => {
      const token = await getToken();
      const qs = new URLSearchParams();
      if (dateRange.start) qs.set('startDate', dateRange.start);
      if (dateRange.end) qs.set('endDate', dateRange.end);
      const qsStr = qs.toString();
      return apiFetch<{
        employee: { id: string; name: string };
        projects: Array<{
          projectId: string;
          projectName: string;
          clientId: string;
          clientName: string;
          billable: boolean;
          totalHours: number;
          eventCount: number;
        }>;
      }>(
        `/api/v1/orgs/${orgId}/dashboard/employees/${employeeId}/projects${qsStr ? `?${qsStr}` : ''}`,
        token,
      );
    },
    enabled: !mockupMode && !!orgId && !!employeeId,
  });

  const appsQuery = useQuery({
    queryKey: ['employeeApps', orgId, employeeId, dateRange],
    queryFn: async () => {
      const token = await getToken();
      const qs = new URLSearchParams();
      if (dateRange.start) qs.set('startDate', dateRange.start);
      if (dateRange.end) qs.set('endDate', dateRange.end);
      const qsStr = qs.toString();
      return apiFetch<{
        employee: { id: string; name: string };
        apps: Array<{ name: string; totalHours: number; eventCount: number; pct: number }>;
        totalHours: number;
      }>(
        `/api/v1/orgs/${orgId}/dashboard/employees/${employeeId}/apps${qsStr ? `?${qsStr}` : ''}`,
        token,
      );
    },
    enabled: !mockupMode && !!orgId && !!employeeId,
  });

  // Task 139: "Time in Jira" section data. Backend endpoint pre-
  // aggregates credential → project → issue rollup; raw worklog rows
  // never cross the boundary. enabled=false path short-circuits on
  // the server so an org with no Jira pays zero query cost.
  const jiraQuery = useQuery({
    queryKey: ['employeeJira', orgId, employeeId, dateRange],
    queryFn: async () => {
      const token = await getToken();
      const qs = new URLSearchParams();
      if (dateRange.start) qs.set('startDate', dateRange.start);
      if (dateRange.end) qs.set('endDate', dateRange.end);
      const qsStr = qs.toString();
      return apiFetch<{
        employee: { id: string; name?: string };
        jira: {
          enabled: boolean;
          lastSyncedAt: string | null;
          groups: JiraProjectGroup[];
        };
      }>(
        `/api/v1/orgs/${orgId}/dashboard/employees/${employeeId}/jira${qsStr ? `?${qsStr}` : ''}`,
        token,
      );
    },
    enabled: !mockupMode && !!orgId && !!employeeId,
  });

  // Task 127: top unmatched (fallback-classified) (app, title) groups for
  // this employee. The backend returns up to `limit` samples ordered by
  // totalHours DESC, plus a `totalGroups` count used to render "see all N".
  const fallbackQuery = useQuery({
    queryKey: ['employeeFallbackSamples', orgId, employeeId, dateRange],
    queryFn: async () => {
      const token = await getToken();
      const qs = new URLSearchParams();
      qs.set('limit', String(REVIEW_ITEMS_LIMIT));
      if (dateRange.start) qs.set('startDate', dateRange.start);
      if (dateRange.end) qs.set('endDate', dateRange.end);
      return apiFetch<EmployeeFallbackSamplesResponse>(
        `/api/v1/orgs/${orgId}/employees/${employeeId}/events/fallback-samples?${qs.toString()}`,
        token,
      );
    },
    enabled: !mockupMode && !!orgId && !!employeeId,
  });

  if (mockupMode) {
    const employee = EMPLOYEES_WITH_STATS.find((e) => e.id === employeeId);
    const jiraFixture: JiraWorklogsFixture =
      JIRA_WORKLOG_FIXTURE[employeeId ?? ''] ?? DEFAULT_JIRA_WORKLOG_FIXTURE;
    return {
      employee,
      stats: EMPLOYEE_DETAIL_STATS,
      clientGroups: EMPLOYEE_CLIENT_GROUPS,
      reviewItems: REVIEW_ITEMS,
      reviewItemsTotal: REVIEW_ITEMS_TOTAL,
      reviewTotalHours: REVIEW_TOTAL_HOURS,
      reviewTotalSessions: REVIEW_TOTAL_SESSIONS,
      appUsage: APP_USAGE,
      activityBars: ACTIVITY_BARS,
      jira: jiraFixture,
      isLoading: false,
    };
  }

  const empData = projectsQuery.data;
  const totalHours = appsQuery.data?.totalHours ?? 0;
  const employee: EmployeeWithStats | undefined = empData
    ? {
        id: empData.employee.id,
        name: empData.employee.name,
        role: 'member',
        businessUnitId: '',
        email: '',
        totalHours,
        billableHours: 0,
        generalHours: 0,
        events: 0,
        billablePercent: 0,
        fullDays: 0,
        halfDays: 0,
        checkIns: 0,
        effectiveDays: 0,
        avgHoursDay: 0,
        avgBillableDay: 0,
      }
    : undefined;

  // Build client groups from projects
  const clientGroupMap = new Map<
    string,
    {
      id: string;
      name: string;
      hours: number;
      events: number;
      projects: NonNullable<typeof empData>['projects'];
    }
  >();
  for (const p of empData?.projects ?? []) {
    const existing = clientGroupMap.get(p.clientId);
    if (existing) {
      existing.hours += p.totalHours;
      existing.events += p.eventCount;
      existing.projects.push(p);
    } else {
      clientGroupMap.set(p.clientId, {
        id: p.clientId,
        name: p.clientName,
        hours: p.totalHours,
        events: p.eventCount,
        projects: [p],
      });
    }
  }

  const projTotalHours = empData?.projects.reduce((s, p) => s + p.totalHours, 0) ?? 1;
  const clientGroups: EmployeeClientGroup[] = [...clientGroupMap.values()].map((cg) => ({
    id: cg.id,
    name: cg.name,
    isLink: true,
    hours: cg.hours,
    events: cg.events,
    pct: projTotalHours > 0 ? (cg.hours / projTotalHours) * 100 : 0,
    projects: cg.projects.map((p) => ({
      name: p.projectName,
      type: p.billable ? ('billable' as const) : ('general' as const),
      hours: p.totalHours,
      events: p.eventCount,
      pct: projTotalHours > 0 ? (p.totalHours / projTotalHours) * 100 : 0,
    })),
  }));

  const appUsage: AppUsageRow[] = (appsQuery.data?.apps ?? []).map((a) => ({
    name: a.name,
    hours: a.totalHours,
    pct: a.pct,
  }));

  // Task 127: map the fallback-sample response to `ReviewItem[]` the
  // existing table component expects. `dateRange` collapses to a single
  // day when first/last occurrence share a calendar day (using the same
  // `formatDayShort` helper the rest of the app uses for short date text).
  const fallbackSamples = fallbackQuery.data?.samples ?? [];
  const reviewItems: ReviewItem[] = fallbackSamples.map((s) => {
    const first = formatDayShort(s.firstOccurredAt);
    const last = formatDayShort(s.lastOccurredAt);
    return {
      app: s.app,
      title: s.title,
      screenshots: s.eventCount,
      sessions: s.sessions,
      time: `${s.totalHours.toFixed(1)}h`,
      lastSeen: last,
      dateRange: first === last ? first : `${first} – ${last}`,
    };
  });
  const reviewItemsTotal = fallbackQuery.data?.totalGroups ?? reviewItems.length;
  // Banner aggregates (task 188): sum across the WHOLE matched set, not just
  // the top-N samples shown in the table. Live mode reads them straight off
  // the response. When the query is still loading we render zero — same
  // shape as `totalGroups === 0`, which hides the section entirely.
  const reviewTotalHours = fallbackQuery.data?.totalHours ?? 0;
  const reviewTotalSessions = fallbackQuery.data?.totalSessions ?? 0;

  const jiraData = jiraQuery.data?.jira;
  const jira = jiraData
    ? {
        enabled: jiraData.enabled,
        lastSyncedAt: jiraData.lastSyncedAt,
        groups: jiraData.groups,
      }
    : { enabled: false, lastSyncedAt: null, groups: [] as readonly JiraProjectGroup[] };

  return {
    employee,
    stats: [],
    clientGroups,
    reviewItems,
    reviewItemsTotal,
    reviewTotalHours,
    reviewTotalSessions,
    appUsage,
    activityBars: [],
    jira,
    isLoading:
      projectsQuery.isLoading ||
      appsQuery.isLoading ||
      fallbackQuery.isLoading ||
      jiraQuery.isLoading,
  };
}
